<template>
  <div class="app-container dida-hotel-mappings">
    <dida-module-header
      title="酒店映射导出"
      description="查询已成功发布到道旅的酒店映射，支持按选中酒店或当前筛选条件安全导出。"
      :status="listError ? 'danger' : 'healthy'"
      :status-text="listError || '映射查询可用'"
      :updated-at="lastRefreshedAt"
      :loading="listLoading"
      @refresh="getList"
    />
    <el-alert
      title="仅展示已成功推送给道旅的酒店。请先勾选酒店，再选择需要写入 Excel 的映射字段。"
      type="info"
      :closable="false"
      show-icon
      class="page-tip"
    />

    <div class="filter-container">
      <el-select
        v-model="listQuery.supplier"
        class="filter-item"
        clearable
        placeholder="供应商"
        size="small"
        style="width: 150px"
      >
        <el-option v-for="item in suppliers" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
      <el-input
        v-model="listQuery.dida_hotel_id"
        class="filter-item"
        clearable
        placeholder="道旅酒店ID（精确）"
        size="small"
        style="width: 210px"
        @keyup.enter.native="handleFilter"
      />
      <el-input
        v-model="listQuery.inn_id"
        class="filter-item"
        clearable
        placeholder="系统 InnID（精确）"
        size="small"
        style="width: 210px"
        @keyup.enter.native="handleFilter"
      />
      <el-button class="filter-item" type="primary" size="small" @click="handleFilter">查询</el-button>
      <el-button class="filter-item" size="small" @click="resetFilter">重置</el-button>
      <el-button
        class="filter-item"
        type="success"
        size="small"
        :disabled="selectedRows.length === 0"
        :loading="exportLoading && exportMode === 'selected'"
        @click="openExportDialog"
      >导出选中酒店（{{ selectedRows.length }}）</el-button>
      <el-button
        class="filter-item"
        type="warning"
        size="small"
        :loading="exportLoading && exportMode === 'filtered'"
        @click="openFilteredExportDialog"
      >按筛选条件全量导出</el-button>
    </div>

    <el-table
      ref="mappingTable"
      v-loading="listLoading"
      :data="list"
      :row-key="rowKey"
      border
      fit
      highlight-current-row
      size="small"
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="selection" width="50" align="center" :reserve-selection="true" />
      <el-table-column label="供应商" width="105" align="center" prop="supplier">
        <template slot-scope="scope">
          <el-tag size="mini" effect="plain" :type="supplierTag(scope.row.supplier)">{{ supplierName(scope.row.supplier) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="道旅酒店ID" min-width="145" prop="dida_hotel_id" show-overflow-tooltip>
        <template slot-scope="scope">{{ displayValue(scope.row.dida_hotel_id) }}</template>
      </el-table-column>
      <el-table-column label="系统 InnID" min-width="145" prop="inn_id" show-overflow-tooltip>
        <template slot-scope="scope">{{ displayValue(scope.row.inn_id) }}</template>
      </el-table-column>
      <el-table-column label="酒店名称" min-width="190" prop="hotel_name" show-overflow-tooltip>
        <template slot-scope="scope">{{ displayValue(scope.row.hotel_name) }}</template>
      </el-table-column>
      <el-table-column label="酒店地址" min-width="260" prop="hotel_address" show-overflow-tooltip>
        <template slot-scope="scope">{{ displayValue(scope.row.hotel_address) }}</template>
      </el-table-column>
      <el-table-column label="锦江映射" min-width="135" prop="jin_jiang_mapping" show-overflow-tooltip>
        <template slot-scope="scope">{{ displayValue(scope.row.jin_jiang_mapping) }}</template>
      </el-table-column>
      <el-table-column label="美团映射" min-width="135" prop="meituan_mapping" show-overflow-tooltip>
        <template slot-scope="scope">{{ displayValue(scope.row.meituan_mapping) }}</template>
      </el-table-column>
      <el-table-column label="携程映射" min-width="135" prop="ctrip_mapping" show-overflow-tooltip>
        <template slot-scope="scope">{{ displayValue(scope.row.ctrip_mapping) }}</template>
      </el-table-column>
      <el-table-column label="艺龙映射" min-width="135" prop="eloon_mapping" show-overflow-tooltip>
        <template slot-scope="scope">{{ displayValue(scope.row.eloon_mapping) }}</template>
      </el-table-column>
    </el-table>

    <div class="pagination-container">
      <el-pagination
        :current-page="listQuery.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="listQuery.limit"
        :total="total"
        background
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <el-dialog
      title="选择 Excel 导出字段"
      :visible.sync="exportDialogVisible"
      :close-on-click-modal="!exportLoading"
      width="560px"
    >
      <el-alert
        :title="exportSummary"
        type="success"
        :closable="false"
        show-icon
        class="export-tip"
      />
      <el-checkbox-group v-model="selectedExportFieldKeys" class="export-fields">
        <el-checkbox
          v-for="field in exportFields"
          :key="field.key"
          :label="field.key"
        >{{ field.label }}</el-checkbox>
      </el-checkbox-group>
      <span slot="footer" class="dialog-footer">
        <el-button :disabled="exportLoading" @click="exportDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="exportLoading" @click="exportExcel">导出 Excel</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import * as XLSX from 'xlsx'
import request from '@/utils/request'
import DidaModuleHeader from './DidaModuleHeader.vue'
import {
  DIDA_HOTEL_MAPPING_EXPORT_FIELDS,
  buildDidaHotelMappingSheetRows,
  selectedExportFields,
} from '@/utils/didaHotelMappingExport'

export default {
  name: 'DidaHotelMappings',
  components: { DidaModuleHeader },
  data() {
    return {
      suppliers: [
        { label: '锦江', value: 'jin_jiang' },
        { label: '龙腾', value: 'long_teng' },
        { label: '隧穿亚朵', value: 'sui_chuan' },
        { label: '丽呈', value: 'li_cheng' },
      ],
      list: [],
      total: 0,
      listLoading: false,
      lastRefreshedAt: '',
      listError: '',
      listRequestId: 0,
      listQuery: {
        page: 1,
        limit: 20,
        supplier: '',
        dida_hotel_id: '',
        inn_id: '',
      },
      selectedRows: [],
      exportDialogVisible: false,
      exportFields: DIDA_HOTEL_MAPPING_EXPORT_FIELDS,
      exportMode: 'selected',
      exportLoading: false,
      exportLoadedCount: 0,
      selectedExportFieldKeys: DIDA_HOTEL_MAPPING_EXPORT_FIELDS.map(field => field.key),
    }
  },
  computed: {
    exportSummary() {
      if (this.exportMode === 'selected') {
        return '将导出已选中的 ' + this.selectedRows.length + ' 家酒店'
      }
      if (this.exportLoading) {
        return '正在按筛选条件读取数据，已加载 ' + this.exportLoadedCount + ' 家酒店'
      }
      return '将按当前筛选条件全量导出，共 ' + this.total + ' 家酒店'
    },
  },
  created() {
    this.getList()
  },
  beforeDestroy() {
    this.listRequestId += 1
  },
  methods: {
    getList() {
      const requestId = ++this.listRequestId
      this.listLoading = true
      this.listError = ''
      return request({
        url: '/api/backend/dida/hotel-mappings',
        method: 'get',
        params: { ...this.listQuery },
      })
        .then((response) => {
          if (requestId !== this.listRequestId) return
          this.list = response.data.items
          this.total = response.data.total
          this.lastRefreshedAt = new Date().toLocaleString()
        })
        .catch(() => {
          if (requestId === this.listRequestId) this.listError = '酒店映射读取失败'
        })
        .finally(() => {
          if (requestId === this.listRequestId) this.listLoading = false
        })
    },
    handleFilter() {
      this.clearSelection()
      this.listQuery.page = 1
      this.getList()
    },
    resetFilter() {
      const limit = this.listQuery.limit
      this.clearSelection()
      this.listQuery = { page: 1, limit, supplier: '', dida_hotel_id: '', inn_id: '' }
      this.getList()
    },
    handleSizeChange(value) {
      this.listQuery.limit = value
      this.listQuery.page = 1
      this.getList()
    },
    handleCurrentChange(value) {
      this.listQuery.page = value
      this.getList()
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows
    },
    clearSelection() {
      this.selectedRows = []
      if (this.$refs.mappingTable) this.$refs.mappingTable.clearSelection()
    },
    rowKey(row) {
      return row.mapping_id
    },
    displayValue(value) {
      return value === null || value === undefined || value === '' ? '-' : value
    },
    supplierName(value) {
      const supplier = this.suppliers.find(item => item.value === value)
      return supplier ? supplier.label : this.displayValue(value)
    },
    supplierTag(value) {
      const tags = { jin_jiang: '', long_teng: 'warning', sui_chuan: 'danger', li_cheng: 'success' }
      return tags[value] || 'info'
    },
    openExportDialog() {
      if (!this.selectedRows.length) {
        this.$message.warning('请先选择要导出的酒店')
        return
      }
      this.exportMode = 'selected'
      this.exportLoadedCount = 0
      this.exportDialogVisible = true
    },
    openFilteredExportDialog() {
      this.exportMode = 'filtered'
      this.exportLoadedCount = 0
      this.exportDialogVisible = true
    },
    async exportExcel() {
      if (this.exportMode === 'selected' && !this.selectedRows.length) {
        this.$message.warning('请先选择要导出的酒店')
        return
      }
      const fields = selectedExportFields(this.selectedExportFieldKeys)
      if (!fields.length) {
        this.$message.warning('请至少选择一个导出字段')
        return
      }

      this.exportLoading = true
      this.exportLoadedCount = 0
      try {
        const rows = this.exportMode === 'selected'
          ? this.selectedRows
          : await this.fetchAllFilteredRows()
        if (!rows.length) {
          this.$message.warning('当前筛选条件没有可导出的酒店')
          return
        }
        this.writeExcel(rows, fields)
        this.exportDialogVisible = false
        this.$message.success('已导出 ' + rows.length + ' 家酒店')
      } catch (error) {
        this.$message.error('全量导出失败，请稍后重试')
      } finally {
        this.exportLoading = false
      }
    },
    async fetchAllFilteredRows() {
      const rows = []
      const filters = {
        supplier: this.listQuery.supplier,
        dida_hotel_id: this.listQuery.dida_hotel_id,
        inn_id: this.listQuery.inn_id,
      }
      let afterId = null
      let hasMore = false
      do {
        const response = await request({
          url: '/api/backend/dida/hotel-mappings/export',
          method: 'get',
          params: {
            ...filters,
            limit: 500,
            after_id: afterId || undefined,
          },
        })
        const batch = response.data.items || []
        rows.push(...batch)
        this.exportLoadedCount = rows.length
        hasMore = Boolean(response.data.has_more)
        const nextAfterId = response.data.next_after_id
        if (hasMore && (!nextAfterId || String(nextAfterId) === String(afterId))) {
          throw new Error('Invalid export cursor')
        }
        afterId = nextAfterId
      } while (hasMore)
      return rows
    },
    writeExcel(rows, fields) {
      const sheet = XLSX.utils.aoa_to_sheet(
        buildDidaHotelMappingSheetRows(rows, this.selectedExportFieldKeys),
      )
      sheet['!cols'] = fields.map(field => ({ wch: field.width }))
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, sheet, '道旅酒店映射')
      XLSX.writeFile(workbook, '道旅酒店映射_' + this.timestamp() + '.xlsx')
    },
    timestamp() {
      const now = new Date()
      const pad = value => String(value).padStart(2, '0')
      return now.getFullYear() + pad(now.getMonth() + 1) + pad(now.getDate()) + '_' +
        pad(now.getHours()) + pad(now.getMinutes()) + pad(now.getSeconds())
    },
  },
}
</script>

<style scoped>
.page-tip {
  margin-bottom: 16px;
}

.filter-container {
  margin-bottom: 12px;
}

.filter-item {
  margin-right: 8px;
  margin-bottom: 8px;
}

.pagination-container {
  padding: 20px 0;
  text-align: right;
}

.export-tip {
  margin-bottom: 18px;
}

.export-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 24px;
}

.export-fields ::v-deep .el-checkbox {
  margin-right: 0;
}

@media (max-width: 700px) {
  .dida-hotel-mappings .filter-item { width: 100% !important; margin-right: 0; }
  .dida-hotel-mappings .pagination-container { overflow-x: auto; text-align: left; }
  .export-fields { grid-template-columns: 1fr; }
}
</style>
